import React from 'react';
import { SectionHelper, Text } from '@wix/design-system';
import {
  appMarketAppPageOneDependencyImpression,
  appMarketAppPageOneDependencyClick,
} from '@wix/bi-logger-app-market-data/v2';
import {
  useTranslation,
  useEssentials,
  useRouterContext,
} from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';
import { DependencyPendingEditorNotificationDataHooks } from './data-hooks';
import type { AppPageType } from '@wix/app-market-core-components-types';

export interface DependencyPendingEditorNotificationProps {
  app: {
    id: string;
    name: string;
  };
  appDependency: {
    id: string;
    name: string;
  };
  appPageType: AppPageType;
}

export function DependencyPendingEditorNotification({
  app,
  appDependency,
  appPageType,
}: DependencyPendingEditorNotificationProps) {
  const { biLogger } = useEssentials();
  const { t } = useTranslation();
  const { router } = useRouterContext();

  React.useEffect(() => {
    biLogger.report(
      appMarketAppPageOneDependencyImpression({
        app_id: app.id,
        dependency_type: 'app',
        app_dependency: appDependency.id,
        type: 'pending',
        app_page_type: appPageType,
      }),
    );
  }, []);

  return (
    <SectionHelper
      layout="horizontal"
      dataHook={DependencyPendingEditorNotificationDataHooks.container}
      appearance="danger"
      fullWidth={true}
      actionText={t('solution.page.appDependencyPending.cta')}
      onAction={() => {
        biLogger.report(
          appMarketAppPageOneDependencyClick({
            app_id: app.id,
            dependency_type: 'app',
            click_type: 'go to editor',
            app_dependency: appDependency.id,
            type: 'pending',
          }),
        );
        router.navigateTo({
          path: RoutePath.EDITOR_COMPLETE_APP_SETUP,
          payload: {
            appId: appDependency.id,
          },
        });
      }}
      title={t('solution.page.appDependencyPending.title', {
        appRequirement: appDependency.name,
      })}
    >
      <Text
        dataHook={DependencyPendingEditorNotificationDataHooks.description}
        size="small"
        weight="thin"
      >
        {t('solution.page.appDependencyPending.subtitle', {
          appDependency: app.name,
          appRequirement: appDependency.name,
          interpolation: { escapeValue: false },
        })}
      </Text>
    </SectionHelper>
  );
}
